import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  ShieldCheck,
  Plus, 
  Trash2, 
  MapPin,
  Eye,
  Loader2,
  Calendar,
  User,
  Users,
  FileText,
  Search,
  Filter,
  Building,
  Send,
  Upload,
  ZoomIn,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  FileImage,
  File,
  ChevronDown,
  ChevronUp,
  Edit3,
  ClipboardCheck,
  AlertCircle,
  Gauge
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status options
const STATUS_OPTIONS = [
  { value: 'compliant', label: 'Compliant', color: 'bg-green-500', icon: CheckCircle2 },
  { value: 'non_compliant', label: 'Non-Compliant', color: 'bg-red-500', icon: XCircle },
  { value: 'overdue', label: 'Overdue', color: 'bg-orange-500', icon: AlertCircle },
  { value: 'pending', label: 'Pending', color: 'bg-blue-500', icon: Clock },
  { value: 'exempt', label: 'Exempt', color: 'bg-gray-500', icon: ShieldCheck },
];

// Category options
const CATEGORY_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'quality', label: 'Quality' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'pressure', label: 'Pressure' },
  { value: 'fire', label: 'Fire' },
  { value: 'health', label: 'Health' },
  { value: 'structural', label: 'Structural' },
  { value: 'hazmat', label: 'Hazmat' },
  { value: 'other', label: 'Other' },
];

// Risk level options
const RISK_LEVELS = [
  { value: 'low', label: 'Low', color: 'text-green-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'critical', label: 'Critical', color: 'text-red-500' },
];

// Remediation status
const REMEDIATION_STATUS = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'verified', label: 'Verified' },
];

// Severity options
const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

// Document types
const DOC_TYPES = [
  { value: 'inspection_record', label: 'Inspection Record' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'test_result', label: 'Test Result' },
  { value: 'photo', label: 'Photo' },
  { value: 'signed_report', label: 'Signed Report' },
  { value: 'audit_finding', label: 'Audit Finding' },
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && <div className="p-3 pt-0 border-t">{children}</div>}
    </div>
  );
};

export function CompliancePanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigate
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState('document');
  const fileInputRef = useRef(null);

  // Form state
  const initialFormState = {
    regulation_name: '',
    regulatory_body: '',
    category: 'other',
    description: '',
    building_id: '',
    floor_area: '',
    applicable_asset_names: '',
    applicable_instrument_names: '',
    process_system: '',
    requirement: '',
    inspection_type: '',
    frequency: '',
    acceptance_criteria: '',
    method_standard: '',
    status: 'pending',
    last_inspection_date: '',
    next_due_date: '',
    grace_period_days: '',
    risk_level: 'low',
    responsible_owner: '',
    performing_party: '',
    approver: '',
    notified_stakeholders: '',
    non_conformance_details: '',
    severity: '',
    corrective_action: '',
    remediation_owner: '',
    target_date: '',
    remediation_status: 'not_required',
    potential_penalty: '',
    remediation_cost: '',
    operational_impact: '',
    downtime_risk: '',
    previous_audits: '',
    notes: '',
    capturePosition: false,
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // Fetch compliance items
  const fetchItems = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/compliance?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching compliance items:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch related data
  const fetchRelatedData = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const [buildingsRes, equipmentRes, instrumentsRes] = await Promise.all([
        fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/equipment?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/instrumentation?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (buildingsRes.ok) setBuildings(await buildingsRes.json());
      if (equipmentRes.ok) setEquipment(await equipmentRes.json());
      if (instrumentsRes.ok) setInstruments(await instrumentsRes.json());
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      fetchRelatedData();
    }
  }, [isOpen, fetchItems, fetchRelatedData]);

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.regulation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.compliance_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.regulatory_body?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    setSubmitting(true);
    try {
      const building = buildings.find(b => b.id === formData.building_id);
      
      const submitData = {
        regulation_name: formData.regulation_name,
        regulatory_body: formData.regulatory_body || null,
        category: formData.category,
        description: formData.description || null,
        building_id: formData.building_id || null,
        building_name: building?.name || null,
        floor_area: formData.floor_area || null,
        applicable_asset_names: formData.applicable_asset_names ? formData.applicable_asset_names.split(',').map(s => s.trim()) : [],
        applicable_instrument_names: formData.applicable_instrument_names ? formData.applicable_instrument_names.split(',').map(s => s.trim()) : [],
        process_system: formData.process_system || null,
        obligation: {
          requirement: formData.requirement || null,
          inspection_type: formData.inspection_type || null,
          frequency: formData.frequency || null,
          acceptance_criteria: formData.acceptance_criteria || null,
          method_standard: formData.method_standard || null,
        },
        status: formData.status,
        last_inspection_date: formData.last_inspection_date || null,
        next_due_date: formData.next_due_date || null,
        grace_period_days: formData.grace_period_days ? parseInt(formData.grace_period_days) : null,
        risk_level: formData.risk_level,
        responsible_owner: formData.responsible_owner || null,
        performing_party: formData.performing_party || null,
        approver: formData.approver || null,
        notified_stakeholders: formData.notified_stakeholders ? formData.notified_stakeholders.split(',').map(s => s.trim()) : [],
        remediation: {
          non_conformance_details: formData.non_conformance_details || null,
          severity: formData.severity || null,
          corrective_action: formData.corrective_action || null,
          remediation_owner: formData.remediation_owner || null,
          target_date: formData.target_date || null,
          status: formData.remediation_status,
        },
        financial: {
          potential_penalty: formData.potential_penalty ? parseFloat(formData.potential_penalty) : null,
          remediation_cost: formData.remediation_cost ? parseFloat(formData.remediation_cost) : null,
          operational_impact: formData.operational_impact || null,
          downtime_risk: formData.downtime_risk || null,
        },
        traceability: {
          previous_audits: formData.previous_audits || null,
          notes: formData.notes || null,
        },
        tour_id: currentTour.id,
      };

      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) submitData.position = cameraPosition.position;
        if (cameraPosition?.rotation) submitData.rotation = cameraPosition.rotation;
      }

      const url = isEditMode 
        ? `${API_URL}/api/compliance/${selectedItem.id}`
        : `${API_URL}/api/compliance`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        if (isEditMode) {
          const updatedItem = await response.json();
          setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
          setSelectedItem(updatedItem);
        } else {
          await fetchItems();
        }
        handleCloseDialog();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save compliance item');
      }
    } catch (error) {
      console.error('Error saving compliance item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setFormData({
      regulation_name: item.regulation_name || '',
      regulatory_body: item.regulatory_body || '',
      category: item.category || 'other',
      description: item.description || '',
      building_id: item.building_id || '',
      floor_area: item.floor_area || '',
      applicable_asset_names: item.applicable_asset_names?.join(', ') || '',
      applicable_instrument_names: item.applicable_instrument_names?.join(', ') || '',
      process_system: item.process_system || '',
      requirement: item.obligation?.requirement || '',
      inspection_type: item.obligation?.inspection_type || '',
      frequency: item.obligation?.frequency || '',
      acceptance_criteria: item.obligation?.acceptance_criteria || '',
      method_standard: item.obligation?.method_standard || '',
      status: item.status || 'pending',
      last_inspection_date: item.last_inspection_date || '',
      next_due_date: item.next_due_date || '',
      grace_period_days: item.grace_period_days || '',
      risk_level: item.risk_level || 'low',
      responsible_owner: item.responsible_owner || '',
      performing_party: item.performing_party || '',
      approver: item.approver || '',
      notified_stakeholders: item.notified_stakeholders?.join(', ') || '',
      non_conformance_details: item.remediation?.non_conformance_details || '',
      severity: item.remediation?.severity || '',
      corrective_action: item.remediation?.corrective_action || '',
      remediation_owner: item.remediation?.remediation_owner || '',
      target_date: item.remediation?.target_date || '',
      remediation_status: item.remediation?.status || 'not_required',
      potential_penalty: item.financial?.potential_penalty || '',
      remediation_cost: item.financial?.remediation_cost || '',
      operational_impact: item.financial?.operational_impact || '',
      downtime_risk: item.financial?.downtime_risk || '',
      previous_audits: item.traceability?.previous_audits || '',
      notes: item.traceability?.notes || '',
      capturePosition: !!item.sweep_id,
    });
    setIsEditMode(true);
    setIsDetailOpen(false);
    setIsCreateDialogOpen(true);
  };

  // Handle status update
  const handleStatusUpdate = async (itemId, newStatus) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/compliance/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updatedItem = await response.json();
        setItems(prev => prev.map(i => i.id === itemId ? updatedItem : i));
        if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!token || !selectedItem || !newComment.trim()) return;
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/${selectedItem.id}/activity?action=comment&details=${encodeURIComponent(newComment.trim())}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token || !selectedItem) return;
    if (file.size > 15 * 1024 * 1024) { alert('File too large. Max 15MB.'); return; }

    setUploadingDoc(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/compliance/${selectedItem.id}/documents/upload?doc_type=${docType}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formDataObj }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document delete
  const handleDeleteDoc = async (docId) => {
    if (!token || !selectedItem || !window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/compliance/${selectedItem.id}/documents/${docId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedDocs = selectedItem.documents.filter(d => d.id !== docId);
        const updatedItem = { ...selectedItem, documents: updatedDocs };
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Handle delete
  const handleDelete = async (itemId) => {
    if (!token || !window.confirm('Delete this compliance item?')) return;
    try {
      const response = await fetch(`${API_URL}/api/compliance/${itemId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setItems(prev => prev.filter(i => i.id !== itemId));
        if (selectedItem?.id === itemId) { setSelectedItem(null); setIsDetailOpen(false); }
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setFormData(initialFormState);
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    if (!option) return null;
    const Icon = option.icon;
    return (
      <Badge className={`${option.color} text-white gap-1`}>
        <Icon className="h-3 w-3" />
        {option.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : null;
  const formatTimestamp = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';
  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) : null;

  // Calculate stats
  const stats = {
    compliant: items.filter(i => i.status === 'compliant').length,
    nonCompliant: items.filter(i => i.status === 'non_compliant').length,
    overdue: items.filter(i => i.status === 'overdue').length,
    pending: items.filter(i => i.status === 'pending').length,
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
          <SheetHeader className="p-6 pb-4 border-b">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <SheetTitle>Compliance</SheetTitle>
            </div>
            <SheetDescription>Regulatory compliance tracking and evidence</SheetDescription>
          </SheetHeader>

          <div className="p-4 border-b space-y-3">
            {/* Status Summary */}
            <div className="flex gap-2">
              <div className="flex-1 p-2 rounded-lg bg-green-500/10 text-center">
                <p className="text-lg font-bold text-green-600">{stats.compliant}</p>
                <p className="text-xs text-muted-foreground">Compliant</p>
              </div>
              <div className="flex-1 p-2 rounded-lg bg-red-500/10 text-center">
                <p className="text-lg font-bold text-red-600">{stats.nonCompliant}</p>
                <p className="text-xs text-muted-foreground">Non-Compliant</p>
              </div>
              <div className="flex-1 p-2 rounded-lg bg-orange-500/10 text-center">
                <p className="text-lg font-bold text-orange-600">{stats.overdue}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
              <div className="flex-1 p-2 rounded-lg bg-blue-500/10 text-center">
                <p className="text-lg font-bold text-blue-600">{stats.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full gap-2">
              <Plus className="h-4 w-4" />New Compliance Item
            </Button>
          </div>

          <ScrollArea className="h-[calc(100vh-380px)]">
            <div className="p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No compliance items found</p>
                </div>
              ) : (
                filteredItems.map(item => (
                  <div key={item.id} className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedItem(item); setIsDetailOpen(true); }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-muted-foreground">{item.compliance_id}</span>
                          {getStatusBadge(item.status)}
                        </div>
                        <h4 className="font-medium truncate">{item.regulation_name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}</Badge>
                          {item.regulatory_body && <span className="text-xs text-muted-foreground">{item.regulatory_body}</span>}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {item.next_due_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Due: {formatDate(item.next_due_date)}</span>}
                          {item.responsible_owner && <span className="flex items-center gap-1"><User className="h-3 w-3" />{item.responsible_owner}</span>}
                          {item.sweep_id && <span className="flex items-center gap-1 text-green-600"><MapPin className="h-3 w-3" />Position saved</span>}
                        </div>
                        {item.risk_level && item.risk_level !== 'low' && (
                          <p className={`text-xs mt-2 flex items-center gap-1 ${RISK_LEVELS.find(r => r.value === item.risk_level)?.color}`}>
                            <AlertTriangle className="h-3 w-3" />{item.risk_level.toUpperCase()} Risk
                          </p>
                        )}
                      </div>
                      {item.sweep_id && onNavigate && (
                        <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); onNavigate(item.sweep_id, { rotation: item.rotation, transitionTime: 1500 }); }} title="Go to location">
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? `Edit ${selectedItem?.compliance_id}` : 'New Compliance Item'}</DialogTitle>
            <DialogDescription>Track regulatory compliance requirements and evidence</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Identity and Scope */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Regulation / Standard Name *</Label>
                <Input value={formData.regulation_name} onChange={(e) => setFormData({...formData, regulation_name: e.target.value})} placeholder="e.g., OSHA 1910.147 - LOTO" required />
              </div>
              <div className="space-y-2">
                <Label>Regulatory Body</Label>
                <Input value={formData.regulatory_body} onChange={(e) => setFormData({...formData, regulatory_body: e.target.value})} placeholder="e.g., OSHA, EPA, API" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description / Requirement Summary</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="What does this regulation require?" rows={2} />
            </div>

            {/* Applicability */}
            <CollapsibleSection title="Applicability & Context" icon={Building} defaultOpen>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Building</Label>
                    <Select value={formData.building_id || 'none'} onValueChange={(value) => setFormData({...formData, building_id: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select building" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Floor / Area / Room</Label>
                    <Input value={formData.floor_area} onChange={(e) => setFormData({...formData, floor_area: e.target.value})} placeholder="e.g., Floor 2, Control Room" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Applicable Assets (comma-separated)</Label>
                  <Input value={formData.applicable_asset_names} onChange={(e) => setFormData({...formData, applicable_asset_names: e.target.value})} placeholder="e.g., Pump P-101, Valve V-202" />
                </div>
                <div className="space-y-2">
                  <Label>Applicable Instrumentation (comma-separated)</Label>
                  <Input value={formData.applicable_instrument_names} onChange={(e) => setFormData({...formData, applicable_instrument_names: e.target.value})} placeholder="e.g., PT-101, TT-202" />
                </div>
                <div className="space-y-2">
                  <Label>Process / System</Label>
                  <Input value={formData.process_system} onChange={(e) => setFormData({...formData, process_system: e.target.value})} placeholder="e.g., Cooling Water System" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Obligation Details */}
            <CollapsibleSection title="Obligation Details" icon={ClipboardCheck}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Compliance Requirement</Label>
                  <Textarea value={formData.requirement} onChange={(e) => setFormData({...formData, requirement: e.target.value})} placeholder="What must be done to stay compliant?" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Inspection / Test Type</Label>
                    <Input value={formData.inspection_type} onChange={(e) => setFormData({...formData, inspection_type: e.target.value})} placeholder="e.g., Visual, Functional Test" />
                  </div>
                  <div className="space-y-2">
                    <Label>Required Frequency</Label>
                    <Input value={formData.frequency} onChange={(e) => setFormData({...formData, frequency: e.target.value})} placeholder="e.g., Monthly, Annually" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Acceptance Criteria</Label>
                  <Input value={formData.acceptance_criteria} onChange={(e) => setFormData({...formData, acceptance_criteria: e.target.value})} placeholder="What defines pass/fail?" />
                </div>
                <div className="space-y-2">
                  <Label>Method / Reference Standard</Label>
                  <Input value={formData.method_standard} onChange={(e) => setFormData({...formData, method_standard: e.target.value})} placeholder="e.g., API 510, ASME B31.3" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Status and Timing */}
            <CollapsibleSection title="Status & Timing" icon={Calendar} defaultOpen>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Compliance Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Risk Level</Label>
                    <Select value={formData.risk_level} onValueChange={(value) => setFormData({...formData, risk_level: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(r => <SelectItem key={r.value} value={r.value}><span className={r.color}>{r.label}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Last Inspection Date</Label>
                    <Input type="date" value={formData.last_inspection_date} onChange={(e) => setFormData({...formData, last_inspection_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Next Due Date</Label>
                    <Input type="date" value={formData.next_due_date} onChange={(e) => setFormData({...formData, next_due_date: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Grace Period (days)</Label>
                  <Input type="number" value={formData.grace_period_days} onChange={(e) => setFormData({...formData, grace_period_days: e.target.value})} placeholder="0" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Ownership */}
            <CollapsibleSection title="Ownership & Accountability" icon={Users}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Responsible Owner</Label>
                    <Input value={formData.responsible_owner} onChange={(e) => setFormData({...formData, responsible_owner: e.target.value})} placeholder="Name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Performing Party</Label>
                    <Input value={formData.performing_party} onChange={(e) => setFormData({...formData, performing_party: e.target.value})} placeholder="Internal / Contractor" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Approver / Authority</Label>
                  <Input value={formData.approver} onChange={(e) => setFormData({...formData, approver: e.target.value})} placeholder="Who signs off?" />
                </div>
                <div className="space-y-2">
                  <Label>Notified Stakeholders (comma-separated)</Label>
                  <Input value={formData.notified_stakeholders} onChange={(e) => setFormData({...formData, notified_stakeholders: e.target.value})} placeholder="e.g., Safety Manager, Operations" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Findings and Remediation */}
            <CollapsibleSection title="Findings & Remediation" icon={AlertTriangle}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Non-Conformance Details</Label>
                  <Textarea value={formData.non_conformance_details} onChange={(e) => setFormData({...formData, non_conformance_details: e.target.value})} placeholder="What went wrong?" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select value={formData.severity || 'none'} onValueChange={(value) => setFormData({...formData, severity: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {SEVERITY_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Remediation Status</Label>
                    <Select value={formData.remediation_status} onValueChange={(value) => setFormData({...formData, remediation_status: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REMEDIATION_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Corrective Action Required</Label>
                  <Textarea value={formData.corrective_action} onChange={(e) => setFormData({...formData, corrective_action: e.target.value})} placeholder="What needs to be done?" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Remediation Owner</Label>
                    <Input value={formData.remediation_owner} onChange={(e) => setFormData({...formData, remediation_owner: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Completion Date</Label>
                    <Input type="date" value={formData.target_date} onChange={(e) => setFormData({...formData, target_date: e.target.value})} />
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Financial */}
            <CollapsibleSection title="Financial & Impact" icon={DollarSign}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Potential Penalty / Fine</Label>
                    <Input type="number" value={formData.potential_penalty} onChange={(e) => setFormData({...formData, potential_penalty: e.target.value})} placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Est. Remediation Cost</Label>
                    <Input type="number" value={formData.remediation_cost} onChange={(e) => setFormData({...formData, remediation_cost: e.target.value})} placeholder="0.00" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Operational Impact</Label>
                  <Textarea value={formData.operational_impact} onChange={(e) => setFormData({...formData, operational_impact: e.target.value})} placeholder="How does non-compliance affect operations?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Downtime Risk</Label>
                  <Input value={formData.downtime_risk} onChange={(e) => setFormData({...formData, downtime_risk: e.target.value})} placeholder="e.g., 8 hours shutdown" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Traceability */}
            <CollapsibleSection title="Traceability & History" icon={FileText}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Previous Audit References</Label>
                  <Textarea value={formData.previous_audits} onChange={(e) => setFormData({...formData, previous_audits: e.target.value})} placeholder="Reference previous audits or inspections" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Notes / Comments</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Additional notes" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            {/* 3D Position */}
            <div className="p-3 rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Link to 3D Position</Label>
                <Button type="button" variant={formData.capturePosition ? "default" : "outline"} size="sm" onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}>
                  {formData.capturePosition ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              {formData.capturePosition ? (
                currentSweep?.id ? (
                  <p className="text-xs text-green-600">✓ Current camera position will be saved</p>
                ) : (
                  <p className="text-xs text-yellow-600">⚠ Navigate to a location in the 3D viewer first</p>
                )
              ) : (
                isEditMode && selectedItem?.sweep_id && (
                  <p className="text-xs text-orange-600">⚠ Saving will clear the existing position</p>
                )
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-mono">{selectedItem.compliance_id}</span>
                    {getStatusBadge(selectedItem.status)}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(selectedItem)}>
                    <Edit3 className="h-4 w-4 mr-2" />Edit
                  </Button>
                </div>
                <DialogTitle>{selectedItem.regulation_name}</DialogTitle>
                {selectedItem.regulatory_body && (
                  <p className="text-sm text-muted-foreground">{selectedItem.regulatory_body}</p>
                )}
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="evidence">Evidence ({selectedItem.documents?.length || 0})</TabsTrigger>
                  <TabsTrigger value="remediation">Remediation</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Label>Status:</Label>
                    <Select value={selectedItem.status} onValueChange={(value) => handleStatusUpdate(selectedItem.id, value)}>
                      <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedItem.description && <div><Label className="text-muted-foreground">Description</Label><p className="mt-1">{selectedItem.description}</p></div>}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><Label className="text-muted-foreground">Category</Label><p>{CATEGORY_OPTIONS.find(c => c.value === selectedItem.category)?.label}</p></div>
                    <div><Label className="text-muted-foreground">Risk Level</Label><p className={RISK_LEVELS.find(r => r.value === selectedItem.risk_level)?.color}>{selectedItem.risk_level?.toUpperCase()}</p></div>
                    {selectedItem.building_name && <div><Label className="text-muted-foreground">Building</Label><p>{selectedItem.building_name}</p></div>}
                    {selectedItem.floor_area && <div><Label className="text-muted-foreground">Floor/Area</Label><p>{selectedItem.floor_area}</p></div>}
                    {selectedItem.next_due_date && <div><Label className="text-muted-foreground">Next Due</Label><p>{formatDate(selectedItem.next_due_date)}</p></div>}
                    {selectedItem.last_inspection_date && <div><Label className="text-muted-foreground">Last Inspection</Label><p>{formatDate(selectedItem.last_inspection_date)}</p></div>}
                    {selectedItem.responsible_owner && <div><Label className="text-muted-foreground">Owner</Label><p>{selectedItem.responsible_owner}</p></div>}
                    {selectedItem.performing_party && <div><Label className="text-muted-foreground">Performing Party</Label><p>{selectedItem.performing_party}</p></div>}
                  </div>

                  {selectedItem.obligation?.requirement && (
                    <div><Label className="text-muted-foreground">Requirement</Label><p className="mt-1 text-sm">{selectedItem.obligation.requirement}</p></div>
                  )}

                  {selectedItem.financial?.potential_penalty && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-200">
                      <Label className="text-red-600">Potential Penalty</Label>
                      <p className="text-lg font-bold text-red-600">{formatCurrency(selectedItem.financial.potential_penalty)}</p>
                    </div>
                  )}

                  {selectedItem.sweep_id && (
                    <Button variant="outline" className="w-full" onClick={() => { onNavigate?.(selectedItem.sweep_id, { rotation: selectedItem.rotation, transitionTime: 1500 }); setIsDetailOpen(false); onClose(); }}>
                      <MapPin className="h-4 w-4 mr-2" />Go to Location
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="evidence" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" />
                      <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="flex-1">
                        {uploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload</>}
                      </Button>
                    </div>

                    {selectedItem.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedItem.documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              {doc.url?.startsWith('data:image') ? <FileImage className="h-5 w-5 text-blue-500" /> : <File className="h-5 w-5 text-gray-500" />}
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {DOC_TYPES.find(t => t.value === doc.doc_type)?.label} • {doc.uploaded_by_name} • {formatTimestamp(doc.uploaded_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {doc.url?.startsWith('data:image') && <Button variant="ghost" size="icon" onClick={() => setSelectedDoc(doc)}><ZoomIn className="h-4 w-4" /></Button>}
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No evidence uploaded yet</p>
                        <p className="text-sm">If it isn&apos;t attached, it didn&apos;t happen</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="remediation" className="mt-4 space-y-4">
                  {selectedItem.remediation?.non_conformance_details ? (
                    <>
                      <div><Label className="text-muted-foreground">Non-Conformance</Label><p className="mt-1">{selectedItem.remediation.non_conformance_details}</p></div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedItem.remediation.severity && <div><Label className="text-muted-foreground">Severity</Label><p>{selectedItem.remediation.severity}</p></div>}
                        <div><Label className="text-muted-foreground">Status</Label><p>{REMEDIATION_STATUS.find(s => s.value === selectedItem.remediation.status)?.label}</p></div>
                        {selectedItem.remediation.remediation_owner && <div><Label className="text-muted-foreground">Owner</Label><p>{selectedItem.remediation.remediation_owner}</p></div>}
                        {selectedItem.remediation.target_date && <div><Label className="text-muted-foreground">Target Date</Label><p>{formatDate(selectedItem.remediation.target_date)}</p></div>}
                      </div>
                      {selectedItem.remediation.corrective_action && <div><Label className="text-muted-foreground">Corrective Action</Label><p className="mt-1">{selectedItem.remediation.corrective_action}</p></div>}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50 text-green-500" />
                      <p>No remediation required</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}><Send className="h-4 w-4" /></Button>
                    </div>

                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {selectedItem.activity?.slice().reverse().map(activity => (
                          <div key={activity.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{activity.user_name}</span>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(activity.timestamp)}</span>
                            </div>
                            <p className="text-sm">
                              {activity.action === 'comment' ? activity.details : activity.action === 'status_changed' ? (
                                <span>Changed status from <Badge variant="outline">{activity.old_value}</Badge> to <Badge variant="outline">{activity.new_value}</Badge></span>
                              ) : (activity.details || activity.action.replace(/_/g, ' '))}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedItem.id)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedDoc && (
            <div className="relative">
              <img src={selectedDoc.url} alt={selectedDoc.name} className="w-full max-h-[80vh] object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-medium">{selectedDoc.name}</p>
                <p className="text-white/70 text-sm">Uploaded by {selectedDoc.uploaded_by_name} • {formatTimestamp(selectedDoc.uploaded_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CompliancePanel;
